import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Breadcrumb } from "../Breadcrumb";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { 
  Download, Filter, TrendingUp, Users, Target, BarChart3, X, Calendar 
} from "lucide-react";
import { exportToCSV } from "../utils/exportUtils";
import { cn } from "../ui/utils";
import { toast } from "sonner";
import API from "../api/api01";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from "recharts";
type StatusStyle = {
  color: string
  badge: string
  icon: string
}

const statusStyles: Record<string, StatusStyle> = {
  Interested: {
    color: "bg-emerald-500",
    badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    icon: "✔",
  },
  "Follow Up": {
    color: "bg-blue-600",
    badge: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
    icon: "📞",
  },
  "Not Interested": {
    color: "bg-red-500",
    badge: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
    icon: "✖",
  },
  "Call Back": {
    color: "bg-amber-500",
    badge: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    icon: "🔁",
  },
}
export function CampaignDashboard() {
  const [selectedTab, setSelectedTab] = useState("channel-summary");
  const [sortBy, setSortBy] = useState("channel");
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);

  const [data, setData] = useState<any>(null);
  const [activities, setActivities] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      try {
        const summaryRes = await API.get("/dashboard/campaign-summary");
        setData(summaryRes.data);
      } catch (err) {
        console.error("Failed to fetch summary data", err);
      }

      try {
        const activityRes = await API.get("/dashboard/activity");
        setActivities(activityRes.data || []);
      } catch (err) {
        console.error("Failed to fetch activity data", err);
      }
      setIsLoading(false);
    };
    fetchDashboardData();
  }, []);

  const trafficChannelData = data?.channel_summary || [];
  const leadDispositionData = data?.lead_disposition || [];
  const courseSourceData = data?.course_source || [];
  const stats = data?.stats || { total_leads: 0, verified_percentage: "0%", paid_applications: 0, enrollment: 0, lost_leads: 0 };

  const filteredTrafficData = trafficChannelData.filter(
    (item: any) => selectedChannels.length === 0 || selectedChannels.includes(item.channel)
  );

  const handleExport = () => {
    const success = exportToCSV(trafficChannelData, "campaign_report.csv");
    if (success) {
      toast.success("Campaign report exported successfully!");
    } else {
      toast.error("Failed to export report.");
    }
  };

  const totalRow = {
    channel: "Total",
    leads: { 
      count: trafficChannelData.reduce((acc: number, curr: any) => acc + (curr.leads?.count || 0), 0), 
      verified: stats.verified_percentage || "0%", 
      mfi: trafficChannelData.reduce((acc: number, curr: any) => acc + (curr.leads?.mfi || 0), 0) 
    },
    paidApplications: { 
      count: stats.paid_applications || 0, 
      conversion: stats.total_leads > 0 ? `${Math.round((stats.paid_applications || 0) / stats.total_leads * 100)}%` : "0%", 
      mfi: trafficChannelData.reduce((acc: number, curr: any) => acc + (curr.paidApplications?.count || 0), 0) 
    },
    enrollment: { 
      count: stats.enrollment || 0, 
      conversion: stats.total_leads > 0 ? `${Math.round((stats.enrollment || 0) / stats.total_leads * 100)}%` : "0%", 
      mfi: trafficChannelData.reduce((acc: number, curr: any) => acc + (curr.enrollment?.count || 0), 0) 
    },
  };

  const chartData = trafficChannelData.map((row: any) => ({
    name: row.channel,
    leads: row.leads?.count ?? 0,
    paidApplications: row.paidApplications?.count ?? 0,
    enrollment: row.enrollment?.count ?? 0
  })).filter((item: any) => selectedChannels.length === 0 || selectedChannels.includes(item.name));


  return (
    <div className="min-h-screen bg-background dark:bg-[#020617] p-4 lg:p-8 animate-in fade-in duration-500">
      <div className="max-w-[1600px] mx-auto space-y-8">
        {/* Modern Minimal Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div className="space-y-1">
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Campaign <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-blue-400"></span> Dashboard
            </h1>
            <p className="text-[13px] font-medium text-muted-foreground">
              Real-time performance analytics and tracking.
            </p>
          </div>
          <div className="flex items-center gap-3">
             <div className="hidden lg:flex items-center gap-2 px-4 py-2 bg-muted/30 rounded-full border border-border/50">
                <Calendar className="h-3.5 w-3.5 text-primary" />
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Fiscal Period: Q2 2026</span>
             </div>
             <Button variant="outline" onClick={handleExport} className="h-10 px-6 rounded-full border-border bg-card hover:bg-accent text-[10px] font-bold uppercase tracking-widest shadow-sm">
                <Download className="h-4 w-4 mr-2 text-primary" />
                Export Data
             </Button>
          </div>
        </div>

        {/* New Professional Horizontal Filter Bar */}
        <div className="flex items-center gap-4 bg-card/30 p-2 rounded-2xl border border-border/40 backdrop-blur-sm overflow-x-auto no-scrollbar no-print">
          <div className="flex items-center gap-2 px-4 border-r border-border/50 mr-2 shrink-0">
             <Filter className="h-3.5 w-3.5 text-primary" />
             <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Channels</span>
          </div>
          <div className="flex gap-2 pb-1 lg:pb-0">
            <button
               onClick={() => setSelectedChannels([])}
               className={cn(
                 "px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all",
                 selectedChannels.length === 0 
                  ? "bg-primary text-white shadow-lg shadow-primary/20" 
                  : "bg-muted/50 text-muted-foreground hover:bg-muted"
               )}
            >
              All Signals
            </button>
            {trafficChannelData.map((item: any) => (
              <button
                key={item.channel}
                onClick={() => {
                  setSelectedChannels(prev => 
                    prev.includes(item.channel) ? [] : [item.channel]
                  );
                }}
                className={cn(
                  "px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all shrink-0 border border-transparent",
                  selectedChannels.includes(item.channel)
                    ? item.channel.toLowerCase() === 'campaign'
                      ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20"
                      : item.channel.toLowerCase() === 'website'
                        ? "bg-amber-500 text-white shadow-lg shadow-amber-500/20"
                        : "bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 shadow-md"
                    : "bg-muted/30 text-muted-foreground hover:border-primary/20 hover:bg-card"
                )}
              >
                {item.channel}
              </button>
            ))}
          </div>
        </div>


        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">

          {/* TOTAL LEADS */}

          <Card className="glass-card group relative overflow-hidden">
            {/* Premium Deep Glass Highlight */}
            <div className="absolute top-0 left-0 w-full h-[3px] transition-all duration-500 opacity-30 group-hover:opacity-100 bg-gradient-to-r from-blue-500/0 via-blue-500 to-blue-500/0" />
            <div className="absolute inset-0 transition-all duration-700 opacity-0 group-hover:opacity-100 bg-gradient-to-br from-blue-500/10 via-transparent to-transparent" />
            
            {/* Darker Tint Overlay on Hover */}
            <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 dark:bg-white/5" />

            <CardContent className="p-6 relative z-10">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Total Leads</p>
                  <p className="text-3xl font-bold group-hover:translate-x-1 transition-transform duration-300">{stats.total_leads.toLocaleString()}</p>
                  <div className="flex items-center gap-1 text-green-600">
                    <TrendingUp className="h-4 w-4" />
                    <span className="text-sm font-medium">+12.5%</span>
                  </div>
                </div>

                <div className="h-12 w-12 rounded-lg transition-all duration-300 group-hover:scale-110 bg-blue-500/10 text-blue-500 group-hover:bg-blue-500 group-hover:text-white flex items-center justify-center group-hover:shadow-lg">
                  <Users className="h-6 w-6" />
                </div>
              </div>
            </CardContent>
          </Card>


          {/* VERIFIED LEADS */}

          <Card className="glass-card group relative overflow-hidden">
            {/* Premium Deep Glass Highlight */}
            <div className="absolute top-0 left-0 w-full h-[3px] transition-all duration-500 opacity-30 group-hover:opacity-100 bg-gradient-to-r from-emerald-500/0 via-emerald-500 to-emerald-500/0" />
            <div className="absolute inset-0 transition-all duration-700 opacity-0 group-hover:opacity-100 bg-gradient-to-br from-emerald-500/10 via-transparent to-transparent" />
            
            {/* Darker Tint Overlay on Hover */}
            <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 dark:bg-white/5" />

            <CardContent className="p-6 relative z-10">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Verified Leads</p>
                  <p className="text-3xl font-bold group-hover:translate-x-1 transition-transform duration-300">{stats.verified_percentage}</p>
                  <div className="flex items-center gap-1 text-green-600">
                    <TrendingUp className="h-4 w-4" />
                    <span className="text-sm font-medium">+5.2%</span>
                  </div>
                </div>

                <div className="h-12 w-12 rounded-lg transition-all duration-300 group-hover:scale-110 bg-emerald-500/10 text-emerald-500 group-hover:bg-emerald-500 group-hover:text-white flex items-center justify-center group-hover:shadow-lg">
                  <Target className="h-6 w-6" />
                </div>
              </div>
            </CardContent>
          </Card>


          {/* PAID APPLICATIONS */}

          <Card className="glass-card group relative overflow-hidden">
            {/* Premium Deep Glass Highlight */}
            <div className="absolute top-0 left-0 w-full h-[3px] transition-all duration-500 opacity-30 group-hover:opacity-100 bg-gradient-to-r from-purple-500/0 via-purple-500 to-purple-500/0" />
            <div className="absolute inset-0 transition-all duration-700 opacity-0 group-hover:opacity-100 bg-gradient-to-br from-purple-500/10 via-transparent to-transparent" />
            
            {/* Darker Tint Overlay on Hover */}
            <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 dark:bg-white/5" />

            <CardContent className="p-6 relative z-10">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Paid Applications</p>
                  <p className="text-3xl font-bold group-hover:translate-x-1 transition-transform duration-300">{stats.paid_applications.toLocaleString()}</p>
                  <div className="flex items-center gap-1 text-green-600">
                    <TrendingUp className="h-4 w-4" />
                    <span className="text-sm font-medium">+8.3%</span>
                  </div>
                </div>

                <div className="h-12 w-12 rounded-lg transition-all duration-300 group-hover:scale-110 bg-purple-500/10 text-purple-500 group-hover:bg-purple-500 group-hover:text-white flex items-center justify-center group-hover:shadow-lg">
                  <BarChart3 className="h-6 w-6" />
                </div>
              </div>
            </CardContent>
          </Card>


          {/* ENROLLMENT */}

          <Card className="glass-card group relative overflow-hidden">
            {/* Premium Deep Glass Highlight */}
            <div className="absolute top-0 left-0 w-full h-[3px] transition-all duration-500 opacity-30 group-hover:opacity-100 bg-gradient-to-r from-amber-500/0 via-amber-500 to-amber-500/0" />
            <div className="absolute inset-0 transition-all duration-700 opacity-0 group-hover:opacity-100 bg-gradient-to-br from-amber-500/10 via-transparent to-transparent" />
            
            {/* Darker Tint Overlay on Hover */}
            <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 dark:bg-white/5" />

            <CardContent className="p-6 relative z-10">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Enrollment</p>
                  <p className="text-3xl font-bold group-hover:translate-x-1 transition-transform duration-300">{stats.enrollment.toLocaleString()}</p>
                  <div className="flex items-center gap-1 text-green-600">
                    <TrendingUp className="h-4 w-4" />
                    <span className="text-sm font-medium">+15.7%</span>
                  </div>
                </div>

                <div className="h-12 w-12 rounded-lg transition-all duration-300 group-hover:scale-110 bg-amber-500/10 text-amber-500 group-hover:bg-amber-500 group-hover:text-white flex items-center justify-center group-hover:shadow-lg">
                  <Users className="h-6 w-6" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* LOST LEADS */}
          <Card className="glass-card group relative overflow-hidden">
            {/* Premium Deep Glass Highlight */}
            <div className="absolute top-0 left-0 w-full h-[3px] transition-all duration-500 opacity-30 group-hover:opacity-100 bg-gradient-to-r from-red-500/0 via-red-500 to-red-500/0" />
            <div className="absolute inset-0 transition-all duration-700 opacity-0 group-hover:opacity-100 bg-gradient-to-br from-red-500/10 via-transparent to-transparent" />
            
            {/* Darker Tint Overlay on Hover */}
            <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 dark:bg-white/5" />

            <CardContent className="p-6 relative z-10">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Lost Leads</p>
                  <p className="text-3xl font-bold group-hover:translate-x-1 transition-transform duration-300">{stats.lost_leads.toLocaleString()}</p>
                  <div className="flex items-center gap-1 text-slate-400">
                    <X className="h-4 w-4" />
                    <span className="text-sm font-medium">Archived</span>
                  </div>
                </div>
                <div className="h-12 w-12 rounded-lg transition-all duration-300 group-hover:scale-110 bg-red-500/10 text-red-500 group-hover:bg-red-500 group-hover:text-white flex items-center justify-center group-hover:shadow-lg">
                  <X className="h-6 w-6" />
                </div>
              </div>
            </CardContent>
          </Card>

        </div>



        {/* Tabs */}
        <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-6">
          <TabsList className="bg-white dark:bg-[#020617] p-1 rounded-lg border border-slate-200 dark:border-slate-800">
            <TabsTrigger value="channel-summary" className="rounded-md">
              Channel Summary
            </TabsTrigger>
            <TabsTrigger value="lead-disposition" className="rounded-md">
              Lead Disposition Summary
            </TabsTrigger>
            <TabsTrigger value="course-source" className="rounded-md">
              Course vs Source
            </TabsTrigger>
            <TabsTrigger value="activities" className="rounded-md">
              Activities
            </TabsTrigger>
          </TabsList>

          {/* Channel Summary Tab */}
          <TabsContent value="channel-summary" className="space-y-6">
            {/* Traffic Channel Table */}
            <Card className="bg-white dark:bg-[#0f172a]">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Traffic Channel</CardTitle>
                <Badge variant="outline" className="font-normal">
                  Last 30 days
                </Badge>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 dark:bg-[#020617] border-b border-slate-200 dark:border-slate-800">
                        <TableHead rowSpan={2} className="font-semibold">
                          Traffic Channel
                        </TableHead>
                        <TableHead colSpan={3} className="text-center border-l border-border font-semibold">
                          Lead
                        </TableHead>
                        <TableHead colSpan={3} className="text-center border-l border-border font-semibold">
                          Paid Applications
                        </TableHead>
                        <TableHead colSpan={3} className="text-center border-l border-border font-semibold">
                          Enrollment
                        </TableHead>
                      </TableRow>
                      <TableRow className="bg-slate-50 dark:bg-[#020617] border-b border-slate-200 dark:border-slate-800">
                        <TableHead className="text-center border-l border-border">Count</TableHead>
                        <TableHead className="text-center">Verified</TableHead>
                        <TableHead className="text-center">MFI</TableHead>
                        <TableHead className="text-center border-l border-border">Count</TableHead>
                        <TableHead className="text-center">Conversion</TableHead>
                        <TableHead className="text-center">MFI</TableHead>
                        <TableHead className="text-center border-l border-border">Count</TableHead>
                        <TableHead className="text-center">Conversion</TableHead>
                        <TableHead className="text-center">MFI</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTrafficData.map((row: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{row.channel}</TableCell>
                          <TableCell className="text-center border-l border-border">{row.leads.count}</TableCell>
                          <TableCell className="text-center">{row.leads.verified}</TableCell>
                          <TableCell className="text-center">{row.leads.mfi}</TableCell>
                          <TableCell className="text-center border-l border-border">{row.paidApplications.count}</TableCell>
                          <TableCell className="text-center">{row.paidApplications.conversion}</TableCell>
                          <TableCell className="text-center">{row.paidApplications.mfi}</TableCell>
                          <TableCell className="text-center border-l border-border">{row.enrollment.count}</TableCell>
                          <TableCell className="text-center">{row.enrollment.conversion}</TableCell>
                          <TableCell className="text-center">{row.enrollment.mfi}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-slate-50 dark:bg-[#020617] font-semibold">
                        <TableCell>{totalRow.channel}</TableCell>
                        <TableCell className="text-center border-l border-border">{totalRow.leads.count}</TableCell>
                        <TableCell className="text-center">{totalRow.leads.verified}</TableCell>
                        <TableCell className="text-center">{totalRow.leads.mfi}</TableCell>
                        <TableCell className="text-center border-l border-border">{totalRow.paidApplications.count}</TableCell>
                        <TableCell className="text-center">{totalRow.paidApplications.conversion}</TableCell>
                        <TableCell className="text-center">{totalRow.paidApplications.mfi}</TableCell>
                        <TableCell className="text-center border-l border-border">{totalRow.enrollment.count}</TableCell>
                        <TableCell className="text-center">{totalRow.enrollment.conversion}</TableCell>
                        <TableCell className="text-center">{totalRow.enrollment.mfi}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Top Channel and Publisher Chart */}
            <Card className="bg-white dark:bg-[#0f172a]">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Top Channel and Publisher</CardTitle>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Select Sort By" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="channel">Sort By Channel</SelectItem>
                    <SelectItem value="leads">Sort By Leads</SelectItem>
                    <SelectItem value="applications">Sort By Applications</SelectItem>
                    <SelectItem value="enrollment">Sort By Enrollment</SelectItem>
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-slate-800" />
                    <XAxis dataKey="name" className="text-xs" angle={-45} textAnchor="end" height={100} />
                    <YAxis className="text-xs" />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="leads" fill="#3B82F6" name="Leads" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="paidApplications" fill="#F59E0B" name="Paid Applications" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="enrollment" fill="#10B981" name="Enrollment" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Lead Disposition Tab */}
          <TabsContent value="lead-disposition" className="space-y-6">

            <Card className="bg-white dark:bg-[#0f172a] border-none shadow-sm">

              <CardHeader>
                <CardTitle>Lead Status Distribution</CardTitle>
              </CardHeader>

              <CardContent>

                <Table>

                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Count</TableHead>
                      <TableHead className="w-[50%]">Percentage</TableHead>
                    </TableRow>
                  </TableHeader>

                  <TableBody>

                    {[...leadDispositionData]
                      .sort(
                        (a: any, b: any) =>
                          parseFloat(b.percentage) - parseFloat(a.percentage)
                      )
                      .map((item: any, index: number) => {

                        const style =
                          statusStyles[item.status] || {
                            color: "bg-slate-500",
                            badge: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
                            icon: "•",
                          }

                        return (

                          <TableRow
                            key={index}
                            className="hover:bg-slate-50 dark:hover:bg-white/[0.04] transition"
                          >

                            <TableCell className="font-medium">

                              <Badge className={`flex items-center gap-2 w-fit ${style.badge}`}>
                                <span>{style.icon}</span>
                                {item.status}
                              </Badge>

                            </TableCell>

                            <TableCell className="font-semibold">
                              {item.count}
                            </TableCell>

                            <TableCell>

                              <div className="space-y-2">

                                <div className="flex items-center justify-between text-sm font-medium">

                                  <span className="text-slate-600 dark:text-slate-400">
                                    Progress
                                  </span>

                                  <span className="text-slate-900 dark:text-white">
                                    {item.percentage}
                                  </span>

                                </div>

                                <div className="w-full bg-slate-200 dark:bg-slate-800 rounded-full h-2.5 overflow-hidden">

                                  <div
                                    className={`${style.color} h-full rounded-full transition-all duration-700 ease-out`}
                                    style={{ width: item.percentage }}
                                  />

                                </div>

                              </div>

                            </TableCell>

                          </TableRow>
                        )
                      })}

                  </TableBody>

                </Table>

              </CardContent>

            </Card>

          </TabsContent>
          {/* Course vs Source Tab */}
          <TabsContent value="course-source" className="space-y-6">
            <Card className="bg-white dark:bg-[#0f172a]">
              <CardHeader>
                <CardTitle>Course Lead Source Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Course</TableHead>
                      <TableHead>Outreach</TableHead>
                      <TableHead>Referral</TableHead>
                      <TableHead>Walk-in</TableHead>
                      <TableHead>Online</TableHead>
                      <TableHead>Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {courseSourceData.map((item: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">
                          <Badge variant="outline">{item.course}</Badge>
                        </TableCell>
                        <TableCell>{item.outreach}</TableCell>
                        <TableCell>{item.referral}</TableCell>
                        <TableCell>{item.walkIn}</TableCell>
                        <TableCell>{item.online}</TableCell>
                        <TableCell className="font-semibold">
                          {item.outreach + item.referral + item.walkIn + item.online}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activities Tab */}
          <TabsContent value="activities" className="space-y-6">
            <Card className="bg-white dark:bg-[#0f172a]">
              <CardHeader>
                <CardTitle>Recent Activities</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative space-y-8 before:absolute before:inset-0 before:ml-5 before:-translate-x-px before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent dark:before:via-slate-800">
                  {activities.map((activity, index) => (
                    <div key={activity.id || index} className="relative flex items-center justify-between md:justify-start md:odd:flex-row md:even:flex-row group">
                      <div className="flex items-center w-full">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white dark:border-slate-900 bg-slate-50 dark:bg-slate-800 text-slate-500 shadow shrink-0 z-10 group-hover:scale-110 transition-transform">
                          {activity.type === 'lead' ? <Users className="h-5 w-5 text-blue-500" /> : <Target className="h-5 w-5 text-purple-500" />}
                        </div>
                        <div className="ml-6 p-4 rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex-1 hover:border-blue-500/30 transition-colors">
                          <div className="flex items-center justify-between mb-1">
                            <div className="font-bold text-slate-900 dark:text-white">{activity.text}</div>
                            <time className="text-xs font-medium text-slate-500">{new Date(activity.time).toLocaleString()}</time>
                          </div>
                          <div className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] uppercase px-1.5 py-0">
                              {activity.status || 'Success'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {activities.length === 0 && (
                    <div className="py-12 text-center text-slate-500">No recent activities found.</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

    </div>
  );
}
